import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Banknote, Download, Lock, Landmark } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Dialog } from '../../components/nursery/accounts/AccountBits';
import { CashCount } from '../../components/nursery/counter/CashCount';
import nurseryCounterService from '../../services/nurseryCounter';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import { useStockSeat } from '../../hooks/useStockSeat';
import { errorMessage } from '../../utils/apiResponse';
import { money, round2 } from '../../utils/nurseryMoney';
import { breakdownTotal, cleanBreakdown, denominationLabel, parseAmount, varianceText } from '../../utils/counterCash';
import { formatDateTimeForInput, formatDateTimeInTz, formatLocalDateInTz, useOrgTimezone } from '../../utils/timezone';
import type { CashBreakdown, TillMovement, TillMovementKind, TillSession } from '../../types/nurseryCounter';

/**
 * One till: its float, every cash movement with who made it, what it should
 * hold, and — once closed — what was counted and the difference. Cash in and
 * cash out need a reason. Closing counts the drawer; a till that is out needs
 * a note. Nothing here can be edited or deleted: a mistake is put right with
 * another movement.
 */

/** Which way a movement moves the till. */
const SIGN: Record<TillMovementKind, 1 | -1> = { sale: 1, refund: -1, pay_in: 1, pay_out: -1 };

const input = 'w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm';

function csvCell(v: unknown): string {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function TillDetail() {
  const { t } = useTranslation('nursery', { keyPrefix: 'TillDetail' });
  const { id } = useParams<{ id: string }>();
  const timezone = useOrgTimezone();
  const { hasAccess: canWork } = useStockSeat('manage');
  const { hasAccess: canReadInvoices } = useModuleLicense('accounts', ['user', 'admin']);
  const [session, setSession] = useState<TillSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<'pay_in' | 'pay_out' | 'close' | 'bank' | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await nurseryCounterService.getTill(Number(id));
      if (res.success) setSession(res.data);
      else setError(errorMessage(res, t('loadFailed')));
    } catch (e) {
      setError(errorMessage(e, t('loadFailed')));
    }
  }, [id, t]);

  useEffect(() => { load(); }, [load]);

  if (!session) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-6">
        {error
          ? <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700 break-words">{error}</div>
          : <div className="h-40 bg-neutral-100 rounded-lg animate-pulse" />}
      </div>
    );
  }

  const totals = session.totals;
  const open = session.status === 'open';
  const movements = session.movements ?? [];

  const exportCsv = () => {
    const rows = [
      [t('csv.time'), t('csv.what'), t('csv.order'), t('csv.amount'), t('csv.handedOver'), t('csv.change'), t('csv.reason'), t('csv.by')],
      ...movements.map((m) => [
        formatDateTimeInTz(m.created_at, timezone), t(`kind.${m.kind}`), m.order_number ?? '',
        (SIGN[m.kind] * m.amount).toFixed(2), m.tendered?.toFixed(2) ?? '', m.change_given?.toFixed(2) ?? '',
        m.reason ?? '', m.created_by_name ?? '',
      ]),
    ];
    const blob = new Blob([rows.map((r) => r.map(csvCell).join(',')).join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `till-${session.till_name.replace(/\W+/g, '-')}-${session.id}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  };

  return (
    <>
      <PageHeader
        title={session.till_name}
        subtitle={open ? t('openSince', { when: formatDateTimeInTz(session.opened_at, timezone) }) : t('closedAt', { when: session.closed_at ? formatDateTimeInTz(session.closed_at, timezone) : '' })}
        icon={<Banknote className="h-6 w-6 text-white" />}
        actionButtons={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={exportCsv}><Download className="h-4 w-4" /> CSV</Button>
            {open && canWork && (
              <>
                <Button variant="outline" size="sm" onClick={() => setDialog('pay_in')}>{t('kind.pay_in')}</Button>
                <Button variant="outline" size="sm" onClick={() => setDialog('pay_out')}>{t('kind.pay_out')}</Button>
                {(totals?.expected_cash ?? 0) > session.opening_float && (
                  <Button variant="outline" size="sm" onClick={() => setDialog('bank')}><Landmark className="h-4 w-4" /> {t('bank.title')}</Button>
                )}
                <Button size="sm" onClick={() => setDialog('close')}><Lock className="h-4 w-4" /> {t('closeTheTill')}</Button>
              </>
            )}
          </div>
        }
      />
      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        <p className="text-sm"><Link to="/nursery/tills" className="text-primary-700 hover:underline">{t('allTills')}</Link></p>

        {totals && (
          <dl className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {([
              [t('stat.float'), totals.opening_float],
              [t('stat.cashSales', { count: totals.sale_count }), totals.sales],
              [t('stat.refunds'), -totals.refunds],
              [t('kind.pay_in'), totals.pay_ins],
              [t('kind.pay_out'), -totals.pay_outs],
              [t('stat.shouldHold'), totals.expected_cash],
            ] as [string, number][]).map(([label, value]) => (
              <div key={label} className="bg-white border border-neutral-200 rounded-xl p-3 min-w-0">
                <dt className="text-xs text-neutral-500 break-words">{label}</dt>
                <dd className="text-lg font-semibold text-neutral-900">{money(value)}</dd>
              </div>
            ))}
          </dl>
        )}

        {!open && (
          <section className={`border rounded-xl p-4 space-y-2 ${session.variance != null && Math.abs(session.variance) >= 0.005 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
            <p className="font-semibold text-neutral-900 break-words">
              {t('countedAgainst', { counted: money(session.counted_cash ?? 0), expected: money(session.expected_cash ?? 0), variance: varianceText(session.variance) })}
            </p>
            <p className="text-sm text-neutral-700 break-words">
              {session.closed_at
                ? t('closedByOn', { name: session.closed_by_name ?? t('someoneGone'), when: formatDateTimeInTz(session.closed_at, timezone) })
                : t('closedBy', { name: session.closed_by_name ?? t('someoneGone') })}
            </p>
            {session.close_notes && <p className="text-sm text-neutral-800 break-words">“{session.close_notes}”</p>}
            {session.closing_breakdown && <BreakdownLine breakdown={session.closing_breakdown} />}
          </section>
        )}

        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-neutral-600 uppercase tracking-wide">{t('everyMovement')}</h2>
          <p className="text-xs text-neutral-500 break-words">
            {t('openedBy', { name: session.opened_by_name ?? t('someoneGone'), amount: money(session.opening_float) })}
          </p>
          {session.opening_breakdown && <BreakdownLine breakdown={session.opening_breakdown} />}
          {movements.length === 0 ? (
            <p className="text-sm text-neutral-500">{t('nothingYet')}</p>
          ) : (
            <ul className="bg-white border border-neutral-200 rounded-xl divide-y divide-neutral-100">
              {movements.map((m) => <MovementRow key={m.id} m={m} timezone={timezone} canReadInvoices={canReadInvoices}
                canRetryBanking={canWork} onChanged={load} />)}
            </ul>
          )}
        </section>
      </div>

      {(dialog === 'pay_in' || dialog === 'pay_out') && (
        <MovementDialog sessionId={session.id} kind={dialog} onClose={() => setDialog(null)}
          onDone={(s) => { setSession(s); setDialog(null); }} />
      )}
      {dialog === 'close' && totals && (
        <CloseDialog session={session} expected={totals.expected_cash} onClose={() => setDialog(null)}
          onDone={(s) => { setSession(s); setDialog(null); }} />
      )}
      {dialog === 'bank' && totals && (
        <BankCashDialog session={session} available={Math.max(0, totals.expected_cash - session.opening_float)}
          onClose={() => setDialog(null)}
          onDone={(s) => { setSession(s); setDialog(null); }} />
      )}
    </>
  );
}

function BreakdownLine({ breakdown }: { breakdown: CashBreakdown }) {
  const { t } = useTranslation('nursery', { keyPrefix: 'TillDetail' });
  const parts = Object.entries(breakdown)
    .sort(([a], [b]) => Number(b) - Number(a))
    .map(([cents, n]) => `${n} × ${denominationLabel(Number(cents))}`);
  return <p className="text-xs text-neutral-600 break-words">{t('countedAs', { parts: parts.join(', ') })}</p>;
}

function MovementRow({ m, timezone, canReadInvoices, canRetryBanking, onChanged }: {
  m: TillMovement;
  timezone: string;
  canReadInvoices: boolean;
  canRetryBanking: boolean;
  onChanged: () => Promise<void>;
}) {
  const { t } = useTranslation('nursery', { keyPrefix: 'TillDetail' });
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const sign = SIGN[m.kind];
  const retry = async () => {
    if (!m.banking_id) return;
    setRetrying(true);
    setRetryError(null);
    try {
      const res = await nurseryCounterService.retryCashBanking(m.banking_id);
      if (res.success) await onChanged();
      else setRetryError(errorMessage(res, t('bank.retryFailed')));
    } catch (e) {
      setRetryError(errorMessage(e, t('bank.retryFailed')));
    }
    setRetrying(false);
  };
  return (
    <li className="px-4 py-3 flex items-start gap-3 min-w-0">
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-neutral-900 break-words">
          {t(`kind.${m.kind}`)}
          {m.order_id && m.order_number && (
            <> · <Link to={`/nursery/orders/${m.order_id}`} className="text-primary-700 hover:underline">{m.order_number}</Link></>
          )}
          {canReadInvoices && m.invoice_id && m.invoice_number && (
            <> · <Link to={`/accounts/invoices/${m.invoice_id}`} className="text-primary-700 hover:underline">{m.invoice_number}</Link></>
          )}
        </p>
        <p className="text-xs text-neutral-500 break-words">
          {formatDateTimeInTz(m.created_at, timezone)}{m.created_by_name ? ` · ${m.created_by_name}` : ''}
          {m.tendered != null ? t('handedOver', { amount: money(m.tendered) }) : ''}
          {m.change_given ? t('changeGiven', { amount: money(m.change_given) }) : ''}
        </p>
        {m.reason && m.kind !== 'refund' && <p className="text-xs text-neutral-700 break-words">{m.reason}</p>}
        {m.banking_id && (
          <div className="mt-1 text-xs text-neutral-600 space-y-1">
            <p className="break-words">
              {t('bank.deposit', { date: m.deposit_date ? formatLocalDateInTz(m.deposit_date, timezone) : '', reference: m.banking_reference || t('notRecorded') })}
            </p>
            <p className={m.xero_transfer_id ? 'text-green-700' : m.xero_push_error ? 'text-red-700' : 'text-amber-800'}>
              {m.xero_transfer_id ? t('bank.xeroDone', { id: m.xero_transfer_id }) : m.xero_push_error ? t('bank.xeroFailed', { error: m.xero_push_error }) : t('bank.xeroPending')}
            </p>
            {canRetryBanking && !m.xero_transfer_id && m.xero_push_error && (
              <Button variant="outline" size="sm" disabled={retrying} onClick={retry}>
                {retrying ? t('bank.retrying') : t('bank.retry')}
              </Button>
            )}
            {retryError && <p className="text-red-700 break-words" role="alert">{retryError}</p>}
          </div>
        )}
      </div>
      <p className={`text-sm font-semibold shrink-0 ${sign > 0 ? 'text-neutral-900' : 'text-red-700'}`}>
        {sign > 0 ? '+' : '−'}{money(m.amount)}
      </p>
    </li>
  );
}

function BankCashDialog({ session, available, onClose, onDone }: {
  session: TillSession;
  available: number;
  onClose: () => void;
  onDone: (s: TillSession) => void;
}) {
  const { t } = useTranslation('nursery', { keyPrefix: 'TillDetail' });
  const timezone = useOrgTimezone();
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(() => formatDateTimeForInput(new Date().toISOString(), timezone).slice(0, 10));
  const [reference, setReference] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const value = parseAmount(amount);
  const dateValue = new Date(`${date}T12:00:00Z`);
  const validDate = /^\d{4}-\d{2}-\d{2}$/.test(date)
    && !Number.isNaN(dateValue.getTime())
    && dateValue.toISOString().slice(0, 10) === date;
  const ok = value != null && value > 0 && value <= available && validDate;

  const save = async () => {
    if (!ok || value == null) return;
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryCounterService.bankCash(session.id, { amount: value, deposit_date: date, reference: reference.trim() || undefined });
      if (res.success) {
        onDone(res.data);
        return;
      }
      setError(errorMessage(res, t('bank.failed')));
    } catch (e) {
      setError(errorMessage(e, t('bank.failed')));
    }
    setBusy(false);
  };

  return (
    <Dialog title={t('bank.title')} subtitle={t('bank.subtitle', { available: money(available), float: money(session.opening_float) })}
      onClose={onClose} footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>{t('cancel')}</Button>
          <Button onClick={save} disabled={!ok || busy}>{busy ? t('bank.recording') : t('bank.record')}</Button>
        </>
      }>
      <p className="text-sm text-neutral-700 break-words">{t('bank.explanation')}</p>
      <div>
        <label htmlFor="bank-cash-amount" className="block text-sm font-medium text-neutral-700 mb-1">{t('bank.amount')}</label>
        <input id="bank-cash-amount" type="text" inputMode="decimal" value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))} className={input} placeholder="0.00" />
      </div>
      {value != null && value > available && <p className="text-sm text-red-700">{t('bank.exceeds', { available: money(available) })}</p>}
      <div>
        <label htmlFor="bank-cash-date" className="block text-sm font-medium text-neutral-700 mb-1">{t('bank.date')}</label>
        <input id="bank-cash-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={input} />
      </div>
      <div>
        <label htmlFor="bank-cash-reference" className="block text-sm font-medium text-neutral-700 mb-1">{t('bank.reference')}</label>
        <input id="bank-cash-reference" type="text" maxLength={200} value={reference}
          onChange={(e) => setReference(e.target.value)} className={input} />
      </div>
      {error && <p className="text-sm text-red-700 break-words" role="alert">{error}</p>}
    </Dialog>
  );
}

function MovementDialog({ sessionId, kind, onClose, onDone }: {
  sessionId: number;
  kind: 'pay_in' | 'pay_out';
  onClose: () => void;
  onDone: (s: TillSession) => void;
}) {
  const { t } = useTranslation('nursery', { keyPrefix: 'TillDetail' });
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const value = parseAmount(amount);
  const ok = value != null && value > 0 && reason.trim().length > 0;

  const save = async () => {
    if (value == null) return;
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryCounterService.addMovement(sessionId, { kind, amount: value, reason: reason.trim() });
      if (res.success) {
        onDone(res.data);
        return;
      }
      setError(errorMessage(res, t('notRecorded')));
    } catch (e) {
      setError(errorMessage(e, t('notRecorded')));
    }
    setBusy(false);
  };

  return (
    <Dialog
      title={kind === 'pay_in' ? t('move.inTitle') : t('move.outTitle')}
      subtitle={kind === 'pay_in' ? t('move.inSubtitle') : t('move.outSubtitle')}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>{t('cancel')}</Button>
          <Button onClick={save} disabled={!ok || busy}>{busy ? t('move.recording') : t('move.record')}</Button>
        </>
      }
    >
      <div>
        <label htmlFor="move-amount" className="block text-sm font-medium text-neutral-700 mb-1">{t('move.amount')}</label>
        <input id="move-amount" type="text" inputMode="decimal" value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ''))} className={input} placeholder="0.00" />
      </div>
      <div>
        <label htmlFor="move-reason" className="block text-sm font-medium text-neutral-700 mb-1">{t('move.reason')}</label>
        <input id="move-reason" type="text" maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} className={input}
          placeholder={kind === 'pay_in' ? t('move.inPlaceholder') : t('move.outPlaceholder')} />
      </div>
      {error && <p className="text-sm text-red-700 break-words" role="alert">{error}</p>}
    </Dialog>
  );
}

function CloseDialog({ session, expected, onClose, onDone }: {
  session: TillSession;
  expected: number;
  onClose: () => void;
  onDone: (s: TillSession) => void;
}) {
  const { t } = useTranslation('nursery', { keyPrefix: 'TillDetail' });
  const [count, setCount] = useState<CashBreakdown>({});
  const [typed, setTyped] = useState('');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const counted = cleanBreakdown(count);
  const value = counted ? breakdownTotal(counted) : parseAmount(typed);
  const variance = value != null ? round2(value - expected) : null;
  const out = variance != null && Math.abs(variance) >= 0.005;
  const ok = value != null && (!out || notes.trim().length > 0);

  const close = async () => {
    if (value == null) return;
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryCounterService.closeTill(session.id, { counted_cash: value, breakdown: counted, notes: notes.trim() || undefined });
      if (res.success) {
        onDone(res.data);
        return;
      }
      setError(errorMessage(res, t('close.failed')));
    } catch (e) {
      setError(errorMessage(e, t('close.failed')));
    }
    setBusy(false);
  };

  return (
    <Dialog
      wide
      title={t('close.title', { name: session.till_name })}
      subtitle={t('close.subtitle', { amount: money(expected) })}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={busy}>{t('cancel')}</Button>
          <Button onClick={close} disabled={!ok || busy}>{busy ? t('close.closing') : t('closeTheTill')}</Button>
        </>
      }
    >
      <CashCount idPrefix="close" value={count} onChange={setCount} denominations={session.denominations} />
      {!counted && (
        <div>
          <label htmlFor="close-typed" className="block text-sm font-medium text-neutral-700 mb-1">{t('close.typed')}</label>
          <input id="close-typed" type="text" inputMode="decimal" value={typed}
            onChange={(e) => setTyped(e.target.value.replace(/[^\d.]/g, ''))} className={input} placeholder="0.00" />
        </div>
      )}
      {value != null && (
        <p className={`text-base font-semibold ${out ? 'text-red-700' : 'text-green-700'}`} role="status">
          {t('close.counted', { amount: money(value), variance: varianceText(variance) })}
        </p>
      )}
      <div>
        <label htmlFor="close-notes" className="block text-sm font-medium text-neutral-700 mb-1">
          {out ? t('close.noteNeeded') : t('close.note')}
        </label>
        <textarea id="close-notes" rows={2} maxLength={2000} value={notes} onChange={(e) => setNotes(e.target.value)} className={input} />
      </div>
      {error && <p className="text-sm text-red-700 break-words" role="alert">{error}</p>}
    </Dialog>
  );
}

export default TillDetail;
